/**
 * PATH: packages/job-hub/src/jobs/qa/itpDocument.ts
 * FILE_ID: JOBHUB-QA-ITP-DOCUMENT-001
 * PURPOSE: Printable ITP HTML that mirrors the popup card: one article
 *      per check, photos in a wrapping contain grid, no Evidence column.
 * WHY: The 7-column table cropped 48px tiles and printed before images
 *      loaded. The popup is the editor; this is a snapshot of that card.
 * RELATED:
 * - JOBHUB-QA-ITP-ITEM-CARD-001
 * - JOBHUB-SCHEDULE-PROGRAMME-PRINT-001
 */
import {
  resolveQaPhotoView,
  type QaPhotoViewSnapshot,
} from '@quotech/ui'
import { itpBrandLockupHtml } from './itpBrandLockup'
import { itpCoverHtml, itpHideList, type ItpCoverFields } from './itpCover'
import { ITP_SIGNOFF_CSS, itpSignoffFor, itpSignoffHtml, type ItpSignoff } from './itpSignoff'
import { checklistGroups, groupEvidenceByItem } from './qaApiHelpers'
import { joinProductNames, resolvedProductNames } from './qaProductResolve'
import type { QaAreaExecution, QaEvidence, QaItemInstance } from './qaTypes'

const EMPTY_PHOTO_VIEWS: QaPhotoViewSnapshot = {
  defaultSize: 72,
  defaultFit: true,
  overrides: {},
}

export interface ItpNote {
  text: string
  author: string
  at: string
}

export interface ItpPhoto {
  id?: string
  url: string
  author: string
  at: string
}

export interface ItpItem {
  id?: string
  areaId?: string
  number: string
  title: string
  product?: string
  acceptanceCriteria?: string
  requirementText?: string
  hasHoldPoint: boolean
  signoff?: ItpSignoff
  status: string
  signedOffBy?: string
  signedOffAt?: string
  notes: ItpNote[]
  photoUrls: ItpPhoto[]
  documents?: string[]
}

export interface ItpChecklist {
  name: string
  heading?: string
  areaName?: string
  band?: { level: string; area: string }
  items: ItpItem[]
}

export function itpSectionTitle(areaName: string, checklistName: string): string {
  return `${areaName} > ${checklistName}`
}

export interface ItpDocumentInput {
  jobNumber: string | number
  levelName?: string
  areaName: string
  checklists: ItpChecklist[]
  reportTitle?: string
  cover?: ItpCoverFields
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Open',
  in_progress: 'Open',
  pending: 'Open',
  done: 'Pass',
  pass: 'Pass',
  further_action: 'F A',
  failed: 'Fail',
  not_applicable: 'N/A',
}

const PRINT_IMAGE_TIMEOUT_MS = 8_000

export type PrintImageProbe = Pick<HTMLImageElement, 'complete' | 'naturalWidth'>

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function itpStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? (status ? status.replace(/_/g, ' ') : 'Open')
}

export function printImageSettled(img: PrintImageProbe): boolean {
  return img.complete
}

export async function waitForPrintImages(
  doc: { images: ArrayLike<PrintImageProbe> },
  timeoutMs = PRINT_IMAGE_TIMEOUT_MS,
  clock: { now: () => number; sleep: (ms: number) => Promise<void> } = {
    now: () => Date.now(),
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  },
): Promise<void> {
  const start = clock.now()
  while (clock.now() - start < timeoutMs) {
    const images = Array.from(doc.images)
    if (images.every(printImageSettled)) return
    await clock.sleep(50)
  }
}

function stamp(author: string, at: string): string {
  const when = at ? at.replace('T', ' ').slice(0, 16) : ''
  const who = author.trim()
  return [who, when].filter(Boolean).join(' · ')
}

function noteLine(note: ItpNote): string {
  const suffix = stamp(note.author, note.at)
  return suffix
    ? `${escapeHtml(note.text)} <span class="meta">${escapeHtml(suffix)}</span>`
    : escapeHtml(note.text)
}

/** `2026-09-16 10:01` under a 120px thumbnail wrapped mid-date. A photo
 *  caption gets the short form, and the timestamp is one unbreakable run so
 *  only the name can wrap. Notes keep the full date — they are full width. */
function compactWhen(at: string): string {
  if (!at) return ''
  const [date, time] = at.replace('T', ' ').slice(0, 16).split(' ')
  const [, month, day] = date.split('-')
  if (!month || !day) return date
  return [`${day}/${month}`, time].filter(Boolean).join(' ')
}

function photoCaption(photo: ItpPhoto): string {
  const who = photo.author.trim()
  const when = compactWhen(photo.at)
  if (!who && !when) return ''
  const parts = [
    who ? escapeHtml(who) : '',
    when ? `<span class="nb">${escapeHtml(when)}</span>` : '',
  ].filter(Boolean)
  return `<figcaption class="meta">${parts.join(' · ')}</figcaption>`
}

/** Every status label prints; the selected one uses a coloured border.
 *  'Open' highlights nothing. done and pass both light Pass. */
export const ITP_PRINT_STATUSES = [
  { label: 'Pass', selectedWhen: ['done', 'pass'] as const, border: '#059669' },
  { label: 'Fail', selectedWhen: ['failed'] as const, border: '#dc2626' },
  { label: 'F A', selectedWhen: ['further_action'] as const, border: '#f97316' },
  { label: 'N/A', selectedWhen: ['not_applicable'] as const, border: '#000000' },
] as const

export function itpStatusChips(status: string): string {
  return ITP_PRINT_STATUSES.map(({ label, selectedWhen, border }) => {
    const on = (selectedWhen as readonly string[]).includes(status)
    if (on) {
      return `<span class="chip chip-on" style="border-color:${border};color:${border}">${escapeHtml(label)}</span>`
    }
    return `<span class="chip">${escapeHtml(label)}</span>`
  }).join('')
}

function checkCard(item: ItpItem, views: QaPhotoViewSnapshot): string {
  const signed = [item.signedOffBy, item.signedOffAt?.slice(0, 10)].filter(Boolean).join(' ')
  const status = itpStatusChips(item.status)
    + (signed ? `<span class="meta">Signed off ${escapeHtml(signed)}</span>` : '')
  const photos = item.photoUrls
    .map((photo) => {
      const view = resolveQaPhotoView(photo.id ?? '', views)
      const fit = view.fit ? 'contain' : 'cover'
      return (
        `<figure style="width:${view.size}px">` +
        `<div class="frame" style="width:${view.size}px;height:${view.size}px">` +
        `<img src="${escapeHtml(photo.url)}" alt="" style="width:100%;height:100%;object-fit:${fit};object-position:${view.panX}% ${view.panY}%;background:#f7f7f7" />` +
        `</div>` +
        `${photoCaption(photo)}</figure>`
      )
    })
    .join('')
  const notes = item.notes.length
    ? `<div class="notes">${item.notes.map((note) => `<div>${noteLine(note)}</div>`).join('')}</div>`
    : ''
  const docs = (item.documents ?? []).length
    ? `<p class="docs">${(item.documents ?? []).map((name) => escapeHtml(name)).join(', ')}</p>`
    : ''
  const product = item.product ? `<div class="meta itp-product">${escapeHtml(item.product)}</div>` : ''
  const requirement = item.requirementText ? `<p class="criteria itp-req">${escapeHtml(item.requirementText)}</p>` : ''
  const acceptance = item.acceptanceCriteria ? `<p class="criteria itp-acc">${escapeHtml(item.acceptanceCriteria)}</p>` : ''
  return (
    `<article class="check"${item.id ? ` data-itp-item="${escapeHtml(item.id)}" data-itp-area="${escapeHtml(item.areaId ?? '')}" data-itp-signoff="${escapeHtml(item.signoff?.state ?? 'none')}"` : ''}>` +
    `<h3><span class="num">${escapeHtml(item.number)}</span> ${item.hasHoldPoint ? '<span class="itp-hold">(Hold Point)</span> ' : ''}${escapeHtml(item.title)}</h3>` +
    product +
    `<div class="status">${status}</div>` +
    itpSignoffHtml(item.signoff) +
    (item.hasHoldPoint && item.signoff && ['needed', 'requested', 'rejected'].includes(item.signoff.state) ? '<button type="button" class="itp-hold-sign" contenteditable="false">Sign</button>' : '') +
    requirement +
    acceptance +
    (photos ? `<div class="photos">${photos}</div>` : '') +
    notes +
    docs +
    '</article>'
  )
}

function sectionFor(checklist: ItpChecklist, views: QaPhotoViewSnapshot): string {
  if (checklist.items.length === 0) return ''
  const band = checklist.band
    ? `<div class="itp-band"><span>${escapeHtml(checklist.band.level)}</span><span>${escapeHtml(checklist.band.area)}</span></div>`
    : ''
  return (
    band +
    `<h2>${escapeHtml(checklist.heading ?? checklist.name)}</h2>` +
    checklist.items.map((item) => checkCard(item, views)).join('')
  )
}

export function buildItpHtml(input: ItpDocumentInput, views?: QaPhotoViewSnapshot): string {
  const resolved = views ?? EMPTY_PHOTO_VIEWS
  const sections = input.checklists.map((list) => sectionFor(list, resolved)).join('')
  const itemCount = input.checklists.reduce((sum, list) => sum + list.items.length, 0)
  const body = itemCount === 0
    ? '<p class="empty">No checks in this plan.</p>'
    : sections
  const printedOn = new Date().toISOString().slice(0, 10)
  const heading = escapeHtml((input.reportTitle ?? '').trim() || 'Inspection Test Plan')
  const areaTitle = escapeHtml(
    [...new Set(input.checklists.map((list) => (list.areaName ?? '').trim()).filter(Boolean))].join(', ')
      || input.areaName.trim()
      || (input.reportTitle ?? '').trim()
      || 'Inspection Test Plan',
  )
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${heading}</title>
<style>
  body { font: 12px/1.45 -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; margin: 0; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .meta { color: #666; font-size: 11px; }
  h2 { font-size: 13px; margin: 18px 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
  .itp-band { display:flex; justify-content:space-between; gap:12px; margin:18px 0 0; font-size:13px; font-weight:600; color:#111; }
  article.check { border: 1px solid #e5e5e5; border-radius: 8px; padding: 12px 14px; margin: 0 0 12px; break-inside: avoid; overflow-wrap: anywhere; }
  article.check h3 { font-size: 13px; margin: 0 0 4px; }
  article.check .num { color: #666; font-weight: 600; }
  article.check .status { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin: 0 0 6px; }
  .chip { border: 1px solid #ccc; border-radius: 6px; padding: 1px 8px; font-size: 11px; color: #555; white-space: nowrap; background: #fff; }
  .chip-on { background: #fff; font-weight: 600; border-width: 2px; }
  article.check .criteria { margin: 0 0 8px; color: #333; }
  body[data-itp-hide~="product"] .itp-product, body[data-itp-hide~="requirement"] .itp-req, body[data-itp-hide~="acceptance"] .itp-acc { display: none }
  .photos figure { display: inline-block; vertical-align: top; margin: 0 8px 8px 0; max-width: 100%; }
  .photos .frame { overflow: hidden; }
  .photos img { display: block; }
  .photos figcaption { margin: 2px 0 0; max-width: 100%; font-size: 10px; line-height: 1.3; overflow-wrap: anywhere; word-break: break-word; }
  .photos figcaption .nb { white-space: nowrap; }
  .notes { margin-top: 8px; }
  .notes .meta { display: block; }
  .docs { margin: 8px 0 0; color: #444; }
  ${ITP_SIGNOFF_CSS}
  .empty { color: #666; margin-top: 18px; }
  .itp-brand { margin: 0 0 14px; max-width: 100%; text-align: center; }
  .itp-org-logo { height: 46px; width: auto; max-width: 100%; display: block; object-fit: contain; margin: 0 auto; }
  .itp-powered-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px; margin: 8px 0 0; }
  .itp-powered { margin: 0; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #666; }
  .itp-quotech-logo { height: 28px; width: auto; max-width: 100%; display: block; object-fit: contain; }
  @page { size: A4; margin: 0; }
  .itp-page { width: 100%; border-collapse: collapse; }
  .itp-page td { padding: 0 12mm; vertical-align: top; }
  .itp-page-pad { height: 12mm; }
  @media print {
    body { margin: 0; }
    /* break-after on every card chained them into one keep-run no page can
       satisfy, so Chrome gave up on all of them and split cards mid-heading.
       Only the section heading needs to stay with what follows it. */
    h2 { break-after: avoid; }
    article.check { break-inside: avoid; }
  }
</style></head>
<body data-itp-hide="${itpHideList(input.cover?.include)}">
${itpCoverHtml(input)}
<table class="itp-page">
<thead><tr><td><div class="itp-page-pad" aria-hidden="true"></div>
${itpBrandLockupHtml(
  (input.cover?.logoSrc ?? '').trim() || undefined,
  input.cover?.logoHeightPx,
)}</td></tr></thead>
<tfoot><tr><td><div class="itp-page-pad" aria-hidden="true"></div></td></tr></tfoot>
<tbody><tr><td>
<h1>${areaTitle}</h1>
<div class="meta">${printedOn} · ${itemCount} check${itemCount === 1 ? '' : 's'}</div>
${body}
</td></tr></tbody>
</table>
</body></html>`
}

export function printItpHtml(html: string): void {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.style.opacity = '0'
  iframe.style.pointerEvents = 'none'
  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    try { iframe.remove() } catch { /* already gone */ }
  }
  iframe.onload = () => {
    const win = iframe.contentWindow
    const doc = iframe.contentDocument ?? win?.document
    if (!win || !doc) {
      cleanup()
      return
    }
    win.addEventListener('afterprint', () => setTimeout(cleanup, 0))
    void waitForPrintImages(doc).then(() => {
      try {
        win.focus()
        win.print()
      } catch {
        cleanup()
      }
    })
    setTimeout(cleanup, 60_000)
  }
  iframe.srcdoc = html
  document.body.appendChild(iframe)
}

function notesFromEvidence(evidence: QaEvidence[]): ItpNote[] {
  return evidence
    .filter((row) => row.type === 'note' && row.note_text)
    .map((row) => ({
      text: row.note_text ?? '',
      author: (row.captured_by_name ?? '').trim(),
      at: row.captured_at ?? '',
    }))
}

function photoSrc(row: QaEvidence): string | null {
  // photo_url is the stable re-signing redirect (carries its own token), so
  // it never expires mid-print. Signed S3 URLs are the fallback.
  return row.photo_url || row.display_url || row.asset_url || null
}

function photosFromEvidence(evidence: QaEvidence[]): ItpPhoto[] {
  const photos: ItpPhoto[] = []
  for (const row of evidence) {
    if (row.type !== 'photo') continue
    const url = photoSrc(row)
    if (!url) continue
    photos.push({
      id: row.id,
      url,
      author: (row.captured_by_name ?? '').trim(),
      at: row.captured_at ?? '',
    })
  }
  return photos
}

function documentsFromEvidence(evidence: QaEvidence[]): string[] {
  return evidence
    .filter((row) => row.type === 'document')
    .map((row) => (row.description || 'Attached file').trim())
}

function toItpItem(
  item: QaItemInstance,
  evidence: QaEvidence[],
  areaNames: string[] = [],
  checklistNames: string[] = [],
  holdPoints: QaAreaExecution['hold_points'] = [],
  areaId = '',
): ItpItem {
  return {
    id: item.id,
    areaId,
    number: item.display_number ?? item.item_number,
    title: item.title,
    product: joinProductNames(resolvedProductNames({
      itemNames: item.product_names,
      itemProduct: item.product,
      checklistNames,
      areaNames,
    })),
    acceptanceCriteria: item.acceptance_criteria,
    requirementText: item.requirement_text,
    hasHoldPoint: !!item.has_hold_point,
    signoff: itpSignoffFor(item, holdPoints),
    status: item.status,
    signedOffBy: item.completed_by_name ?? undefined,
    signedOffAt: item.completed_at ?? undefined,
    notes: notesFromEvidence(evidence),
    photoUrls: photosFromEvidence(evidence),
    documents: documentsFromEvidence(evidence),
  }
}

export function buildItpInputFromExecutions(
  areas: QaAreaExecution[],
  jobNumber: string | number,
  checklistIds?: string[] | null,
  itemIds?: ReadonlySet<string>,
  reportTitle?: string,
): ItpDocumentInput {
  const wanted = checklistIds ? new Set(checklistIds) : null
  const first = areas[0]
  return {
    jobNumber,
    reportTitle,
    levelName: areas.length === 1 ? first?.floor_name : undefined,
    areaName: areas.length === 1
      ? (first?.group_name ? `${first.group_name} - ${first.area_name}` : (first?.area_name ?? ''))
      : `${areas.length} areas`,
    checklists: areas.flatMap((area) => {
      const byItem = groupEvidenceByItem(area.evidence ?? [])
      const lists = checklistGroups(area, undefined)
        .filter((group) => (wanted ? wanted.has(group.checklist.id) : true))
        .map((group) => {
          const items = itemIds
            ? group.items.filter((item) => itemIds.has(item.id))
            : group.items
          return { group, items }
        })
        .filter((row) => row.items.length > 0)
      return lists.map(({ group, items }, index) => ({
        name: group.name,
        areaName: area.group_name ? `${area.group_name} - ${area.area_name}` : area.area_name,
        heading: group.name,
        band: index === 0
          ? { level: area.floor_name ?? '', area: area.area_name }
          : undefined,
        items: items.map((item) => toItpItem(
          item,
          byItem[item.id] ?? [],
          area.product_names,
          group.checklist.product_names,
          area.hold_points,
          area.id,
        )),
      }))
    }),
  }
}

export function buildItpInputFromExecution(
  area: QaAreaExecution,
  jobNumber: string | number,
  checklistIds?: string[] | null,
  itemIds?: ReadonlySet<string>,
): ItpDocumentInput {
  return buildItpInputFromExecutions([area], jobNumber, checklistIds, itemIds)
}

/** The HTML Print shows. Save PDF must send this string and nothing else. */
export function buildItpRequestHtml(
  areas: QaAreaExecution[],
  jobNumber: string | number,
  areaIds: readonly string[],
  checklistIds: string[] | null | undefined,
  itemIds: ReadonlySet<string> | undefined,
  reportTitle: string | undefined,
  views?: QaPhotoViewSnapshot,
  cover?: ItpCoverFields,
): string {
  const input = buildItpInputFromExecutions(
    areas.filter((area) => areaIds.includes(area.id)),
    jobNumber,
    checklistIds ?? undefined,
    itemIds,
    reportTitle,
  )
  input.cover = cover
  return buildItpHtml(input, views)
}
